import { Button } from "antd";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { reaction } from "mobx";
import { observer } from "mobx-react";
import { ProjectUsersStore } from "Pages/Admin/Stores/ProjectUsersStore";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { ProjectMembersEditor } from "./ProjectMembersEditor";

interface ProjectMembersModalProps extends ModalComponentProps {
  projectId: number;
  prGroupId?: string;
}

export const ProjectMembersModal = observer(
  ({ projectId, prGroupId, hideModal, setConfirmOnCancel }: ProjectMembersModalProps) => {
    const { t } = useTranslation(I18N_NS.ADMIN);
    const store = useMemo(() => new ProjectUsersStore(), []);
    const [saving, setSaving] = useState(false);

    // ModalRoot recreates setConfirmOnCancel on every provider render and each call updates
    // provider state, so it must never be an effect dependency.
    const setConfirmOnCancelRef = useRef(setConfirmOnCancel);
    setConfirmOnCancelRef.current = setConfirmOnCancel;

    useEffect(() => {
      const disposeDirtyReaction = reaction(
        () => store.isDirty,
        (isDirty) => setConfirmOnCancelRef.current?.(isDirty)
      );
      void store.fetch(projectId);

      return () => {
        disposeDirtyReaction();
        store.clear();
      };
    }, [projectId, store]);

    const disableConfirmOnCancel = async () => {
      await Promise.resolve(setConfirmOnCancelRef.current?.(false));
    };

    const handleSave = async () => {
      try {
        setSaving(true);
        await store.saveUsers(projectId);
        await disableConfirmOnCancel();
        notificationSuccess(t("Success"), t("Project members updated"));
        hideModal?.();
      } finally {
        setSaving(false);
      }
    };

    const handleCancel = async () => {
      store.resetChanges();
      await disableConfirmOnCancel();
      hideModal?.();
    };

    return (
      <div>
        <ProjectMembersEditor
          store={store}
          getUserEditUrl={(user) =>
            prGroupId ? `/admin/pg/${prGroupId}/user/edit/${user.id}` : `/admin/user/edit/${user.id}`
          }
          projectGroupId={prGroupId ? Number(prGroupId) : undefined}
          tableName="pgMembers"
        />
        <ButtonsRow justify="space-between">
          <Button onClick={() => void handleCancel()}>{t("Cancel")}</Button>
          <Button type="primary" loading={saving} disabled={!store.isDirty} onClick={() => void handleSave()}>
            {t("Save")}
          </Button>
        </ButtonsRow>
      </div>
    );
  }
);
